import { useSelector } from "react-redux";
import {
    IAdminToggleBtn,
    IReportTypes,
    ITargetCellRenderer,
} from "@ts/common-types";
import { Select } from "antd";
import { useEffect, useState } from "react";
import { FaCheck, FaTimes } from "react-icons/fa";
import { showToast } from "@/ts/modals";
import * as Sentry from "@sentry/react";
import { updateReportType } from "@/ts/retrieve-data";
import { titleCase } from "@/ts/utils";
import usePermissionCheck from "@/hooks/usePermissionCheck";

const ReportTypeCellRenderer: React.FC<ITargetCellRenderer> = (params) => {
    const [reportType, setReportType] = useState({
        originalValue: null,
        currentValue: null
    });
    const reportTypesData = useSelector(
        (state: IReportTypes) => state.reportTypesData.value
    );
    const enableRevstack = useSelector(
        (state: IAdminToggleBtn) => state.enableRevstack.value
    );
    const [actionBtnVisible, setActionBtnVisible] = useState(false);
    const [spinnerDisp, setSpinnerDisp] = useState(false);

    useEffect(() => {
        const reportTypeName = getReportTypeName(params.data?.report_type_id);
        setReportType({
            originalValue: reportTypeName,
            currentValue : reportTypeName
        });
    },[params.data]);


    const getReportTypeName = (id: number) => {
        const name =
            reportTypesData?.filter((type) => type.id === id)[0]?.name || "";
        return `${id} - ${name}`;
    };

    const handleOnChange = (val: string) => {
        setActionBtnVisible(!(val === reportType.originalValue))
        setReportType((prev) => ({
            ...prev,
            currentValue: val
        }));
    };

    const actionBtnHandler = async (action: string) => {
        if(action === "cancel"){
            setReportType((prev) => ({
                ...prev,
                currentValue: prev.originalValue
            }));
            setActionBtnVisible(false);
            return;
        }
        const reportId = params.data?.report_id;
        const reportTypeId = +reportType.currentValue.split("-")[0].trim();
        try {
            setSpinnerDisp(true);
            const response = await updateReportType(reportTypeId, reportId, params.existingReportsData);
            if (!response.status) {
                throw new Error(response.message);
            }
            showToast("success", response.message);
            params.setGetUpdatedReports((prev) => !prev);
        } catch (err) {
            console.error(err);
            showToast("danger", (err as Error).message);
            Sentry.captureException(err);
        } finally {
            setSpinnerDisp(false);
        }
    }

    const { isModuleEditDisabled } = usePermissionCheck({ type: "cellRenderer" });

    return (
        <>
            <div
                id={`${params.data?.report_id}-parent-cell-div`}
                style={{ display: "flex", alignItems: "center" }}
            >
                <Select
                    className="d-flex"
                    placeholder="Select Report Type"
                    value={reportType.currentValue}
                    key={params.data?.report_id}
                    onChange={(val) => handleOnChange(val)}
                    showSearch
                    disabled={isModuleEditDisabled}
                >
                    {reportTypesData?.map((type) => {
                        const typeValue = `${type.id} - ${type.name}`;
                        if (!enableRevstack && type.id === 1 && reportType.currentValue !== typeValue) {
                            return null;
                        }
                        return (
                            <option
                                key={type.id}
                                value={typeValue}
                            >
                                {titleCase(type.name)}
                            </option>
                        );
                    })}
                </Select>
                <div
                    id={`${params.data?.report_id}-edit-div`}
                    style={{
                        display: actionBtnVisible && !spinnerDisp ? "flex" : "none",
                    }}
                >
                    <button
                        id={`${params.data?.report_id}-apply-edit-btn`}
                        className="btn-sm btn-light mx-1 p-2"
                        style={{
                            padding: "4px 8px",
                            display: "flex",
                            alignItems: "center",
                            border: "1px solid #B2B2B2",
                            borderRadius: "6px",
                        }}
                        onClick={() => {
                            actionBtnHandler("update");
                        }}
                    >
                        <FaCheck
                            className="text-primary"
                            style={{ fontSize: "15px" }}
                        />
                    </button>

                    <button
                        id={`${params.data?.report_id}-cancel-edit-btn`}
                        className="btn-sm btn-light p-2"
                        style={{
                            padding: "4px 9px",
                            display: "flex",
                            alignItems: "center",
                            border: "1px solid #B2B2B2",
                            borderRadius: "6px",
                        }}
                        onClick={() => {
                            actionBtnHandler("cancel");
                        }}
                    >
                        <FaTimes style={{ fontSize: "15px" }} />
                    </button>
                </div>
                <button
                    className="spinner-border text-primary ms-2"
                    style={{ display: `${!spinnerDisp ? "none" : "unset"}` }}
                ></button>
            </div>
        </>
    );
};

export default ReportTypeCellRenderer;
